<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { AppState, SetNotification, ToastQueue } from '../../etc/stores';
  import { formatMoney, isMobile, validateEmail } from '../../etc/utils';
  import Modal from '../templates/RoundedModal.svelte';
  import Button from '../form/Button.svelte';
  import AnimatedCoin from './AnimatedCoin.svelte';
  import Heading from '../typography/Heading.svelte';
  import Label from '../typography/Label.svelte';
  import TextInput from '../form/TextInput.svelte';
  import { Container } from '../../services/container';
  import { Auth } from '../../services/auth';
  import { WidgetConnect } from '../../services/widget-connect';
  import { Logger } from '../../services/logger';
  import { SessionManager } from '../../services/session-manager';
  import { useModal } from '../../etc/hooks/useModal';
  import Analytics from '../../services/analytics';
  import PostcheckoutHowItWorks from './PostcheckoutHowItWorks.svelte';
  import PostCheckoutModalBG from '../icons/PostCheckoutModalBG.svelte';
  import Pencil from '../icons/Pencil.svelte';
  import { replaceVariables } from '../../i18n';
  import { extractMessageFromError } from '../../services/api';
  import AuthConnector from './AuthConnector.svelte';
  import sessionKeys from '../../etc/sessionKeys';

  const auth = Container.get(Auth);
  const connect = Container.get(WidgetConnect);
  const logger = Container.get(Logger).context(
    'PostCheckoutModal.svelte'
  ) as Logger;
  const session = Container.get(SessionManager) as SessionManager;

  const category = `soft-signup-${Analytics.getCategoryLoginState()}`;
  const sufixGA = Analytics.getIsGuest() ? '-guest' : '-logged';

  const { close } = useModal();

  const t = (text: string, values?: Record<string, string | number>) => {
    return replaceVariables($_(`postcheckout_modal.${text}`), values);
  };

  let isHowItWorks = false;

  let handleConnectCustomer;
  let setLoading;

  let error = '';

  let providerEmail =
    $AppState.checkout.contactEmail || $AppState.auth.providerData?.email;

  let email = providerEmail ? providerEmail : '';
  let canChangeEmail = !providerEmail;

  const businessName = $AppState.business.name;
  const firstName =
    $AppState.auth.providerData?.firstName ||
    $AppState.checkout.contactFirstName ||
    undefined;
  const lastName =
    $AppState.auth.providerData?.lastName ||
    $AppState.checkout.contactLastName ||
    undefined;

  const transactionToken =
    session.get('checkout')?.transactionToken ||
    $AppState.checkout.transactionToken;

  const onClose = () => {
    SetNotification('remindPostCheckout', true);
  };

  const navigateHowItWorks = (toHowItWorks: boolean) => {
    isHowItWorks = toHowItWorks;
  };

  const changeEmail = () => {
    canChangeEmail = true;
  };

  const isValid = () => {
    if (canChangeEmail && !validateEmail(email)) {
      error = t('invalid-email-error');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    Analytics.logEvent(`click-collect-coins${sufixGA}`, category);

    if (!isValid()) return;

    setLoading(true);

    const { account } = await auth.getAuthState();

    const data = {
      account,
      loginEmail: email,
      firstName,
      lastName,
      transactionToken
    };

    try {
      if (await auth.isAuthenticatedOnWebsite()) {
        setLoading(false);
        await handleConnectCustomer();
      } else {
        const {
          next,
          data: { auth: authPayload }
        } = await connect.postSignUpEmail(data);
        const access_token = authPayload?.access_token;

        if (next === 'login-form' || !access_token) {
          alreadyHaveAccount();
          setLoading(false);
          return;
        }

        await auth.authWithAccessToken(access_token);
        localStorage.setItem(sessionKeys.AUTH_SOFT_LOGIN, access_token);
        Analytics.logEvent('create-password-send-email', category);
      }

      close();

      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          popoverOpen: true,
          popoverRouteOnOpen: {
            route: '/soft-login',
            navigationOptions: {
              state: {
                completeProfileInfomation: !firstName || !lastName
              }
            }
          }
        }
      }));

      Analytics.logEvent('click-collect-coins-success', category);

      session.set(sessionKeys.COLLECT_COINS, true);
      session.remove(sessionKeys.CHECKOUT);
      session.remove(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL);
    } catch (e) {
      setLoading(false);

      Analytics.logEvent('click-collect-coins-error', category, undefined, {
        errorMessage: e.message,
        errorStack: e.stack,
        error: e
      });

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });

      logger.error(e);
    }
  };

  const onInput = (e: CustomEvent<any>) => {
    canChangeEmail = true;
    email = e.detail;
    error = '';
  };

  const onAfterConnectCustomer = async () => {
    session.set(sessionKeys.COLLECT_COINS, true);
    session.remove(sessionKeys.CHECKOUT);
    session.remove(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL);
    return {
      skipNavigate: true
    };
  };

  const alreadyHaveAccount = async () => {
    if (await auth.isAuthenticatedOnWebsite()) {
      Analytics.logEvent('click-collect-coins-logged', category);
      await handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      Analytics.logEvent('click-collect-coins-guest', category);
      auth.openLoginModal({ mode: 'signup' });
    }
  };

  const triggerConnectCustomer = () => {
    handleConnectCustomer();
  };

  onMount(() => {
    sessionStorage.removeItem('@twism:purchase');
  });
</script>

<AuthConnector
  {onAfterConnectCustomer}
  {triggerConnectCustomer}
  loggerContext="PostCheckoutModal.svelte"
  let:loading
  bind:handleConnectCustomer
  bind:setLoading
>
  <Modal showCloseButton={!isHowItWorks} on:close={onClose}>
    <div class="twism-postcheckout-modal">
      {#if isHowItWorks}
        <div
          class="twism-postcheckout-modal--content"
          in:fade={{ duration: 1000, delay: 600 }}
          out:fade={{ duration: 500 }}
        >
          <PostcheckoutHowItWorks
            on:back={() => navigateHowItWorks(false)}
            {email}
            {error}
            {loading}
            on:changeEmail={onInput}
            on:submit={handleSubmit}
            on:signin={alreadyHaveAccount}
          />
        </div>
      {:else}
        <div
          class="twism-postcheckout-modal--content twism--scrollable"
          in:fade|local={{ duration: 700, delay: 500 }}
          out:fade
        >
          <div class="twism-postcheckout-modal--header">
            <PostCheckoutModalBG />
            <div class="twism-postcheckout-modal--header--coin">
              <AnimatedCoin
                image={$AppState.business.logo.publicPath.large}
                scale={isMobile() ? '1' : '1.4'}
              />
            </div>
          </div>
          <div class="twism-postcheckout-modal--body">
            <div class="twism-postcheckout-modal--body--top">
              <Heading
                class="twism-postcheckout-modal--subtitle"
                textAlign="center"
              >
                {@html t('subtitle', { name: firstName || lastName })}
              </Heading>
              <Heading
                class="twism-postcheckout-modal--title"
                textAlign="center"
              >
                {@html t('title', {
                  amount: formatMoney(
                    $AppState.business.location,
                    $AppState.website.coins || 0
                  ),
                  businessName
                })}
              </Heading>
              <div
                class="twism-postcheckout-modal--actions {canChangeEmail
                  ? '-twism-change-email'
                  : ''}"
              >
                {#if canChangeEmail}
                  <TextInput
                    label=""
                    value={email}
                    on:input={onInput}
                    name="customer-email"
                    type="text"
                    placeholder={t('input-placeholder')}
                    {error}
                    removeIconsTopOffset
                  />
                {/if}
                <div
                  class="twism-postcheckout-modal--btn {error
                    ? '-twism-top-margin'
                    : ''}"
                  style="background: {`${$AppState.theme.colors.illustration.secondary}1a`}"
                >
                  <Button
                    class="twism-postcheckout-modal--btn-component"
                    {loading}
                    on:click={handleSubmit}
                  >
                    <Heading
                      component="p"
                      type="h3"
                      color={$AppState.theme.colors.textAboveBackground}
                    >
                      {t('button')}
                    </Heading>
                  </Button>
                  {#if !canChangeEmail}
                    <div class="twism-postcheckout-modal--email">
                      <Label type="medium-regular"
                        >{t('receiver-email')}&nbsp;</Label
                      >
                      <div
                        class="twism-postcheckout-modal--email--action"
                        on:click={changeEmail}
                      >
                        <Label type="medium-regular-semibold">
                          <a href={'javascript:void(0)'}>{email}</a>
                        </Label>
                        <span class="twism-postcheckout-modal--email--icon">
                          <Pencil />
                        </span>
                      </div>
                    </div>
                  {/if}
                </div>
              </div>
            </div>
            <div
              class="twism-postcheckout-modal--terms"
              class:--change-email={canChangeEmail}
            >
              <Label type="medium-regular">{@html t('terms')}</Label>
              <div class="twism-postcheckout-modal--powered">
                <Label type="medium-regular">
                  <a
                    href={'javascript:void(0)'}
                    on:click={() => navigateHowItWorks(true)}
                  >
                    {t('loyalty-program', { businessName })}
                  </a>
                  <br class="twism-postcheckout-modal--mobile-only" />
                  {@html t('powered-by')}
                </Label>
              </div>
            </div>
          </div>
        </div>
      {/if}
      <div
        class="twism-circular-reveal {isHowItWorks
          ? 'twism-circular-reveal--in'
          : ''}"
      />
    </div>
  </Modal>
</AuthConnector>

<style lang="scss">
  .twism-postcheckout-modal {
    width: 768px;
    height: 551px;
    padding-bottom: 24px;
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    box-sizing: border-box;

    &--mobile-only {
      display: none;

      @media screen and (max-width: 468px) {
        display: block;
      }
    }

    &--content {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;

      &.twism--scrollable {
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      @media screen and (max-width: 468px) {
        border-top-right-radius: 20px;
        border-top-left-radius: 20px;
        display: flex;
        flex-direction: column;
      }
    }

    &--body {
      width: 100%;

      @media screen and (max-width: 468px) {
        height: calc(100% - 135px);
        box-sizing: border-box;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }

    &--header {
      width: 100%;
      position: relative;
      margin-bottom: 34px;

      &--coin {
        position: absolute;
        top: 38px;
        left: 50%;
        transform: translateX(-50%);
      }
    }

    &--email {
      margin: 10px 0;
      display: flex;
      align-items: flex-end;
      justify-content: center;

      @media screen and (max-width: 468px) {
        flex-flow: column nowrap;
        align-items: center;
      }

      &--action {
        display: flex;
        justify-content: center;
        cursor: pointer;

        
        @media screen and (max-width: 468px) {
          :global(p) {
            overflow-wrap: break-word;
            max-width: calc(100% - 70px);
          }
        }
      }

      &--icon {
        margin-left: 4px;
        height: 20px;
      }
    }

    &--actions {
      display: flex;
      align-self: center;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin: 32px auto 0;
      width: 527px;

      &.-twism-change-email {
        margin-top: 32px;
      }

      @media screen and (max-width: 468px) {
        margin-top: 46px;

        @media screen and (max-height: 700px) {
          margin-top: 24px;
        }
      }
    }

    &--btn {
      width: 100%;
      border-radius: 12px;
      margin-top: 8px;
    }

    &--terms {
      margin-top: 84px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0.7;

      &.--change-email {
        margin-top: 68px;
      }

      b {
        font-weight: 600;
      }

      @media screen and (max-width: 468px) {
        margin-top: 50px;
        padding-bottom: 24px;

        @media screen and (max-height: 700px) {
          margin-top: 40px;
        }
      }
    }

    &--powered {
      margin-top: 8px;
    }

    :global(.twism-postcheckout-modal--actions .input) {
      width: 100%;
    }

    :global(.twism-postcheckout-modal--actions .input__field) {
      width: 100%;
      height: 56px;
      border-radius: 12px;
      border-width: 2px;
      padding: 18px 28px 18px 12px;
      margin: 0;
      box-sizing: border-box;
    }

    :global(.twism-postcheckout-modal--title) {
      font-size: 32px;
      line-height: 39px;
      letter-spacing: -0.005em;

      @media screen and (max-width: 468px) {
        font-size: 20px;
        line-height: 26px;
      }
    }

    :global(.twism-postcheckout-modal--title strong) {
      color: var(--twism-theme-primary200);
    }

    :global(.twism-postcheckout-modal--subtitle) {
      font-weight: 400;
      line-height: 36px;
      margin-bottom: 4px;

      @media screen and (max-width: 468px) {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: -0.005em;
      }
    }

    :global(.twism-postcheckout-modal--terms p) {
      @media screen and (max-width: 468px) {
        font-size: 11px;
        line-height: 16px;
        text-align: center !important;
      }
    }

    @media screen and (max-width: 468px) {
      width: 100%;
      height: 100%;

      &--body {
        padding: 0 24px 24px;
      }

      &--header {
        margin-bottom: 64px;
        :global(svg) {
          border-top-right-radius: 20px;
          border-top-left-radius: 20px;
        }

        &--coin {
          top: 27px;
        }
      }

      &--actions {
        width: 100%;
      }
    }
  }

  :global(.twism-postcheckout-modal--btn-component) {
    height: 56px !important;
    border-radius: 12px !important;
    box-shadow: none !important;

    @media screen and (max-width: 468px) {
      height: 48px !important;
    }
  }

  :global(.twism-postcheckout-modal--btn-component p) {
    @media screen and (max-width: 468px) {
      font-size: 16px;
      line-height: 22px;
    }
  }

  :global(.twism-postcheckout-modal a) {
    color: var(--twism-theme-typography);
    text-decoration: underline;
  }

  .twism-circular-reveal {
    position: absolute;
    width: 150%;
    height: 150%;
    left: 50%;
    bottom: -75%;
    border-radius: 75%;
    background: var(--twism-theme-primary600);
    pointer-events: none;
    transform-origin: left;
    transform: scale(0) translateX(-50%);
    opacity: 1;

    @media screen and (max-width: 468px) {
      width: 500px;
      height: 500px;
    }

    &.twism-circular-reveal--in {
      transition: transform 1s cubic-bezier(0.65, 0, 0.35, 1),
        0.3s opacity 0.7s ease-in-out;
      transform: scale(2) translateX(-50%);
      opacity: 0;

      @media screen and (max-width: 468px) {
        transform: scale(4) translateX(-50%);
      }
    }
  }
</style>
